// === IMPORTS ===
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  Trash2,
  Paintbrush,
  PlusCircle,
  Layers,
  User,
  Copy,
  Check,
  RotateCcw,
  Armchair,
  Box,
  MapPin,
  ExternalLink,
  Zap,
  ChevronRight,
} from 'lucide-react';
import {
  OfficeLayout,
  PlacedFurniture,
  AIAgent,
  OfficeRoom,
  IsoTileType,
  FurnitureType,
  FloorMaterialOption,
} from '../types';
import {
  DEFAULT_FLOOR_MATERIALS,
  FURNITURE_CATALOG,
  DEFAULT_ROOM_TEMPLATES,
  DEFAULT_MCP_TOOLS,
  RoomTemplate,
} from '../data/officeCatalog';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import '../styles/twin-icons-shared.css';
import './TileContextMenu.css';

export interface TileContextMenuProps {
  col: number;
  row: number;
  x: number;
  y: number;
  layout: OfficeLayout;
  floorMaterials?: FloorMaterialOption[];
  onClose: () => void;
  onClearTile: (col: number, row: number) => void;
  onClearTileFurniture?: (col: number, row: number) => void;
  onClearTileFloor?: (col: number, row: number) => void;
  onClearTileAgent?: (col: number, row: number) => void;
  onSetTileFloor?: (col: number, row: number, tileType: IsoTileType) => void;
  onPlaceFurniture?: (type: FurnitureType, col: number, row: number) => void;
  onMoveAgentToTile?: (agentId: string, col: number, row: number) => void;
  onSelectRoom?: (roomId: string | null) => void;
  onDeleteRoom?: (roomId: string) => void;
  onStartRoomDraft?: (col: number, row: number) => void;
  onCreateRoom?: (col: number, row: number, template?: RoomTemplate | Partial<OfficeRoom>) => void;
  onSelectAgent?: (agent: AIAgent | null) => void;
  onSelectItem?: (item: PlacedFurniture | null) => void;
}

// === COMPONENTE: MENÚ CONTEXTUAL DE TILE (CLIC DERECHO EN EL GRID) ===
export const TileContextMenu: React.FC<TileContextMenuProps> = ({
  col,
  row,
  x,
  y,
  layout,
  floorMaterials = DEFAULT_FLOOR_MATERIALS,
  onClose,
  onClearTile,
  onClearTileFurniture,
  onClearTileFloor,
  onClearTileAgent,
  onSetTileFloor,
  onPlaceFurniture,
  onMoveAgentToTile,
  onSelectRoom,
  onDeleteRoom,
  onStartRoomDraft,
  onCreateRoom,
  onSelectAgent,
  onSelectItem,
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);

  const [activeSubmenu, setActiveSubmenu] = useState<'floor' | 'furniture' | 'agents' | 'room' | null>(null);
  const floorTriggerRef = useRef<HTMLDivElement | null>(null);
  const furnitureTriggerRef = useRef<HTMLDivElement | null>(null);
  const agentsTriggerRef = useRef<HTMLDivElement | null>(null);
  const roomTriggerRef = useRef<HTMLDivElement | null>(null);

  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: y, left: x });
  const [submenuStyle, setSubmenuStyle] = useState<{
    side: 'right' | 'left';
    verticalAlign: 'top' | 'bottom';
  }>({ side: 'right', verticalAlign: 'top' });

  const [confirmAction, setConfirmAction] = useState<{
    type: 'room' | 'furniture' | 'agent' | 'tile';
    title: string;
    name?: string;
    icon?: string;
    description?: string;
    warningText?: string;
    confirmText?: string;
    onConfirm: () => void;
  } | null>(null);

  // === QUÉ HAY EN ESTE TILE ===
  const floorKeyComma = `${col},${row}`;
  const floorKeyUnderscore = `${col}_${row}`;
  const customFloor = layout.customFloors
    ? layout.customFloors[floorKeyComma] || layout.customFloors[floorKeyUnderscore]
    : null;

  const currentFloorType: IsoTileType = customFloor?.tileType || layout.floorType || 'blue_checkered';
  const currentFloorOption = floorMaterials.find((f) => f.id === currentFloorType) || {
    id: currentFloorType,
    name: currentFloorType,
    icon: '🏢',
    previewColor: '#334155',
  };

  const itemOnTile = layout.items.find((item) => {
    const w = item.width || 1;
    const h = item.height || 1;
    return col >= item.col && col < item.col + w && row >= item.row && row < item.row + h;
  });

  const itemDef = itemOnTile ? FURNITURE_CATALOG.find((f) => f.type === itemOnTile.type) : null;

  const agentOnTile = layout.agents.find((ag) => ag.currentCol === col && ag.currentRow === row);

  const roomOnTile = (layout.rooms || []).find(
    (r) => col >= r.col && col < r.col + r.width && row >= r.row && row < r.row + r.height
  );

  // === POSICIONAMIENTO DENTRO DEL VIEWPORT ===
  useLayoutEffect(() => {
    const winWidth = window.innerWidth;
    const winHeight = window.innerHeight;
    const menuEl = menuRef.current;

    const estimatedWidth = menuEl ? menuEl.offsetWidth : 260;
    const estimatedHeight = menuEl ? menuEl.offsetHeight : 360;

    let posX = x;
    let posY = y;

    if (x + estimatedWidth > winWidth - 12) posX = Math.max(12, x - estimatedWidth);
    if (y + estimatedHeight > winHeight - 12) posY = Math.max(12, y - estimatedHeight);

    setMenuPos({ left: posX, top: posY });
  }, [x, y]);

  // === POSICIONAMIENTO DE SUBMENÚS EN CASCADA ===
  useLayoutEffect(() => {
    if (!activeSubmenu) return;

    const winWidth = window.innerWidth;
    const winHeight = window.innerHeight;
    const triggerEl =
      activeSubmenu === 'floor' ? floorTriggerRef.current :
      activeSubmenu === 'furniture' ? furnitureTriggerRef.current :
      activeSubmenu === 'agents' ? agentsTriggerRef.current :
      roomTriggerRef.current;

    if (!triggerEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const submenuWidth = 240;
    const submenuHeight = 280;

    let side: 'right' | 'left' = 'right';
    if (triggerRect.right + submenuWidth + 8 > winWidth) side = 'left';

    let verticalAlign: 'top' | 'bottom' = 'top';
    if (triggerRect.top + submenuHeight > winHeight - 12) verticalAlign = 'bottom';

    setSubmenuStyle({ side, verticalAlign });
  }, [activeSubmenu, menuPos]);

  // === TEMPORIZADORES PARA CIERRE SUAVE ===
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) { clearTimeout(closeTimeoutRef.current); closeTimeoutRef.current = null; }
    if (submenuTimeoutRef.current) { clearTimeout(submenuTimeoutRef.current); submenuTimeoutRef.current = null; }
  };

  const handleMouseLeave = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => { onClose(); }, 600);
  };

  const handleOpenSubmenu = (menu: 'floor' | 'furniture' | 'agents' | 'room') => {
    if (submenuTimeoutRef.current) { clearTimeout(submenuTimeoutRef.current); submenuTimeoutRef.current = null; }
    if (closeTimeoutRef.current) { clearTimeout(closeTimeoutRef.current); closeTimeoutRef.current = null; }
    setActiveSubmenu(menu);
  };

  const handleCloseSubmenuDelayed = () => {
    if (submenuTimeoutRef.current) clearTimeout(submenuTimeoutRef.current);
    submenuTimeoutRef.current = setTimeout(() => { setActiveSubmenu(null); }, 300);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (submenuTimeoutRef.current) clearTimeout(submenuTimeoutRef.current);
    };
  }, []);

  // === CIERRE POR CLIC AFUERA / ESCAPE / RESIZE ===
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const handleWindowResize = () => onClose();

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [onClose]);

  const handleCopyCoords = () => {
    navigator.clipboard.writeText(`[${col}, ${row}]`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const popularFurniture: FurnitureType[] = [
    'desk_single', 'executive_desk', 'meeting_table', 'sofa', 'lounge_chair',
    'plant_monstera', 'whiteboard', 'coffee_station', 'water_dispenser', 'toilet',
    'wall_brick', 'wall_glass', 'door_single',
  ];

  // === RENDER ===
  return (
    <div
      ref={menuRef}
      style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
      className="twin-tilemenu"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* === CABECERA DE INFORMACIÓN === */}
      <div className="twin-tilemenu-header">
        <div className="twin-tilemenu-header-row">
          <div className="twin-tilemenu-coords">
            <MapPin className="twin-icon-sm twin-tilemenu-color-cyan" />
            <span>Tile [{col}, {row}]</span>
          </div>
          <button type="button" onClick={handleCopyCoords} title="Copy tile coordinates" className="twin-tilemenu-copy-btn">
            {copied ? (
              <><Check className="twin-icon-xs twin-stat-positive" /><span className="twin-stat-positive">Copied!</span></>
            ) : (
              <><Copy className="twin-icon-xs" /><span>Copy</span></>
            )}
          </button>
        </div>

        <div className="twin-tilemenu-info-row">
          {roomOnTile ? (
            <span
              className="twin-tilemenu-room-tag"
              style={{ backgroundColor: `${roomOnTile.color}20`, color: roomOnTile.color, borderColor: `${roomOnTile.color}40` }}
              title={roomOnTile.name}
            >
              <span>{roomOnTile.icon || '🏷️'}</span>
              <span className="twin-tilemenu-room-tag-name">{roomOnTile.name}</span>
            </span>
          ) : (
            <span className="twin-tilemenu-no-zone">No assigned zone</span>
          )}

          <span className="twin-tilemenu-dot">&bull;</span>
          <span className="twin-tilemenu-floor-info">
            <span>{currentFloorOption.icon}</span>
            <span>{currentFloorOption.name}</span>
            {customFloor && <span className="twin-tilemenu-custom-tag">Custom</span>}
          </span>
        </div>

        {(itemOnTile || agentOnTile) && (
          <div className="twin-tilemenu-occupants-box">
            {itemOnTile && (
              <div className="twin-tilemenu-occupant-stack">
                <div className="twin-tilemenu-occupant-row twin-tilemenu-color-amber">
                  <Box className="twin-icon-xs" />
                  <span className="twin-tilemenu-occupant-name">{itemOnTile.label || itemDef?.name || itemOnTile.type}</span>
                </div>
                {(() => {
                  const mcpId = itemOnTile.mcpToolId || itemDef?.mcpToolId;
                  if (!mcpId) return null;
                  const tool = DEFAULT_MCP_TOOLS.find((t) => t.id === mcpId);
                  return (
                    <div className="twin-tilemenu-mcp-tag">
                      <Zap className="twin-icon-xs" />
                      <span>MCP: {tool?.name || mcpId}</span>
                    </div>
                  );
                })()}
              </div>
            )}
            {agentOnTile && (
              <div className="twin-tilemenu-occupant-row twin-tilemenu-color-emerald">
                <User className="twin-icon-xs" />
                <span>{agentOnTile.name} ({agentOnTile.role})</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="twin-tilemenu-body">
        {/* === ACCIÓN PRINCIPAL: LIMPIAR TODO === */}
        <button type="button" onClick={() => { onClearTile(col, row); onClose(); }} className="twin-tilemenu-clear-all-btn">
          <div className="twin-tilemenu-clear-all-icon"><Trash2 className="twin-icon-sm" /></div>
          <div className="twin-tilemenu-clear-all-text">
            <span className="twin-tilemenu-clear-all-title">Clear everything on this tile</span>
            <span className="twin-tilemenu-clear-all-sub">Removes resource, agent, and custom floor</span>
          </div>
        </button>

        {/* === RESET RÁPIDO DE PISO === */}
        {customFloor && (
          <button
            type="button"
            onClick={() => { if (onClearTileFloor) onClearTileFloor(col, row); else onClearTile(col, row); onClose(); }}
            className="twin-tilemenu-reset-floor-btn"
            title="Resets custom floor on this tile and returns to building base floor"
          >
            <div className="twin-tilemenu-action-row-left">
              <RotateCcw className="twin-icon-sm" />
              <span>Reset Floor on this Tile</span>
            </div>
            <span className="twin-tilemenu-reset-tag">Reset</span>
          </button>
        )}

        <div className="twin-tilemenu-divider" />

        {/* === 1. SUBMENÚ: PISO === */}
        <div ref={floorTriggerRef} className="twin-tilemenu-submenu-wrap" onMouseEnter={() => handleOpenSubmenu('floor')} onMouseLeave={handleCloseSubmenuDelayed}>
          <button
            type="button"
            onClick={() => setActiveSubmenu((prev) => (prev === 'floor' ? null : 'floor'))}
            className={`twin-tilemenu-trigger-row twin-tilemenu-trigger--cyan ${activeSubmenu === 'floor' ? 'twin-tilemenu-trigger--active-cyan' : ''}`}
          >
            <div className="twin-tilemenu-action-row-left">
              <Paintbrush className="twin-icon-sm twin-tilemenu-color-cyan" />
              <span>Change Tile Floor</span>
            </div>
            <ChevronRight className="twin-icon-xs twin-tilemenu-chevron" />
          </button>

          {activeSubmenu === 'floor' && (
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleCloseSubmenuDelayed}
              style={{
                [submenuStyle.side === 'right' ? 'left' : 'right']: '100%',
                [submenuStyle.side === 'right' ? 'paddingLeft' : 'paddingRight']: '6px',
                ...(submenuStyle.verticalAlign === 'top' ? { top: '-4px' } : { bottom: '-4px' }),
              }}
              className="twin-tilemenu-cascade twin-tilemenu-cascade--cyan"
            >
              <div className="twin-tilemenu-cascade-inner">
                <div className="twin-tilemenu-cascade-title twin-tilemenu-color-cyan">Floor Materials</div>
                <div className="twin-tilemenu-cascade-list" onWheel={(e) => e.stopPropagation()}>
                  {floorMaterials.map((mat) => (
                    <button
                      key={mat.id}
                      type="button"
                      onClick={() => { if (onSetTileFloor) onSetTileFloor(col, row, mat.id); onClose(); }}
                      className={`twin-tilemenu-cascade-item twin-tilemenu-cascade-item--cyan ${currentFloorType === mat.id && customFloor ? 'twin-tilemenu-cascade-item--selected-cyan' : ''}`}
                    >
                      <div className="twin-tilemenu-action-row-left">
                        <span>{mat.icon}</span>
                        <span>{mat.name}</span>
                      </div>
                      {currentFloorType === mat.id && customFloor && <Check className="twin-icon-xs twin-tilemenu-color-cyan" />}
                    </button>
                  ))}

                  {customFloor && (
                    <>
                      <div className="twin-tilemenu-divider" />
                      <button
                        type="button"
                        onClick={() => { if (onClearTileFloor) onClearTileFloor(col, row); else onClearTile(col, row); onClose(); }}
                        className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--amber"
                      >
                        <RotateCcw className="twin-icon-xs" />
                        <span>Reset floor on this tile</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* === 2. SUBMENÚ: MOBILIARIO === */}
        <div ref={furnitureTriggerRef} className="twin-tilemenu-submenu-wrap" onMouseEnter={() => handleOpenSubmenu('furniture')} onMouseLeave={handleCloseSubmenuDelayed}>
          <button
            type="button"
            onClick={() => setActiveSubmenu((prev) => (prev === 'furniture' ? null : 'furniture'))}
            className={`twin-tilemenu-trigger-row twin-tilemenu-trigger--amber ${activeSubmenu === 'furniture' ? 'twin-tilemenu-trigger--active-amber' : ''}`}
          >
            <div className="twin-tilemenu-action-row-left">
              <Box className="twin-icon-sm twin-tilemenu-color-amber" />
              <span>Place Resource Here</span>
            </div>
            <ChevronRight className="twin-icon-xs twin-tilemenu-chevron" />
          </button>

          {activeSubmenu === 'furniture' && (
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleCloseSubmenuDelayed}
              style={{
                [submenuStyle.side === 'right' ? 'left' : 'right']: '100%',
                [submenuStyle.side === 'right' ? 'paddingLeft' : 'paddingRight']: '6px',
                ...(submenuStyle.verticalAlign === 'top' ? { top: '-4px' } : { bottom: '-4px' }),
              }}
              className="twin-tilemenu-cascade twin-tilemenu-cascade--amber twin-tilemenu-cascade--wide"
            >
              <div className="twin-tilemenu-cascade-inner">
                <div className="twin-tilemenu-cascade-title twin-tilemenu-color-amber">Common Resources</div>
                <div className="twin-tilemenu-cascade-list twin-tilemenu-cascade-list--tall" onWheel={(e) => e.stopPropagation()}>
                  {popularFurniture.map((fType) => {
                    const def = FURNITURE_CATALOG.find((f) => f.type === fType);
                    if (!def) return null;
                    return (
                      <button
                        key={def.type}
                        type="button"
                        onClick={() => { if (onPlaceFurniture) onPlaceFurniture(def.type, col, row); onClose(); }}
                        className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--amber"
                      >
                        <span>{def.icon}</span>
                        <div className="twin-tilemenu-furniture-item-text">
                          <span className="twin-tilemenu-furniture-item-name">{def.name}</span>
                          <span className="twin-tilemenu-furniture-item-size">{def.width}x{def.height} tiles</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* === 3. SUBMENÚ: AGENTES === */}
        <div ref={agentsTriggerRef} className="twin-tilemenu-submenu-wrap" onMouseEnter={() => handleOpenSubmenu('agents')} onMouseLeave={handleCloseSubmenuDelayed}>
          <button
            type="button"
            onClick={() => setActiveSubmenu((prev) => (prev === 'agents' ? null : 'agents'))}
            className={`twin-tilemenu-trigger-row twin-tilemenu-trigger--emerald ${activeSubmenu === 'agents' ? 'twin-tilemenu-trigger--active-emerald' : ''}`}
          >
            <div className="twin-tilemenu-action-row-left">
              <User className="twin-icon-sm twin-tilemenu-color-emerald" />
              <span>{agentOnTile ? 'Manage Agent' : 'Assign / Move Agent'}</span>
            </div>
            <ChevronRight className="twin-icon-xs twin-tilemenu-chevron" />
          </button>

          {activeSubmenu === 'agents' && (
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleCloseSubmenuDelayed}
              style={{
                [submenuStyle.side === 'right' ? 'left' : 'right']: '100%',
                [submenuStyle.side === 'right' ? 'paddingLeft' : 'paddingRight']: '6px',
                ...(submenuStyle.verticalAlign === 'top' ? { top: '-4px' } : { bottom: '-4px' }),
              }}
              className="twin-tilemenu-cascade twin-tilemenu-cascade--emerald twin-tilemenu-cascade--wider"
            >
              <div className="twin-tilemenu-cascade-inner">
                <div className="twin-tilemenu-cascade-title-row twin-tilemenu-color-emerald">
                  <span>{agentOnTile ? `Agent: ${agentOnTile.name}` : 'Place on this Tile'}</span>
                  <span className="twin-tilemenu-cascade-coords">({col}, {row})</span>
                </div>

                <div className="twin-tilemenu-cascade-list" onWheel={(e) => e.stopPropagation()}>
                  {agentOnTile ? (
                    <>
                      <button type="button" onClick={() => { if (onSelectAgent) onSelectAgent(agentOnTile); onClose(); }} className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--emerald">
                        <ExternalLink className="twin-icon-sm" />
                        <span>Open Profile of {agentOnTile.name}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setConfirmAction({
                            type: 'agent',
                            title: 'Remove Agent from Board?',
                            name: agentOnTile.nickname?.trim() || agentOnTile.name,
                            icon: '🤖',
                            description: `Do you want to remove "${agentOnTile.nickname?.trim() || agentOnTile.name}" from the grid and return them to the available pool?`,
                            confirmText: 'Remove Agent',
                            onConfirm: () => { if (onClearTileAgent) onClearTileAgent(col, row); else onClearTile(col, row); onClose(); },
                          });
                        }}
                        className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--amber"
                      >
                        <Trash2 className="twin-icon-sm" />
                        <span>Remove from grid (return to pool)</span>
                      </button>
                    </>
                  ) : layout.agents.length > 0 ? (
                    layout.agents.map((ag) => {
                      const isPlaced = ag.isPlaced !== false && ag.currentCol >= 0 && ag.currentRow >= 0;
                      return (
                        <button key={ag.id} type="button" onClick={() => { if (onMoveAgentToTile) onMoveAgentToTile(ag.id, col, row); onClose(); }} className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--emerald">
                          <div className="twin-tilemenu-agent-item-left">
                            <div className="twin-tilemenu-agent-dot" style={{ backgroundColor: ag.avatarShirtColor }}>
                              {ag.role.charAt(0).toUpperCase()}
                            </div>
                            <span className="twin-tilemenu-agent-name">{ag.name}</span>
                          </div>
                          {isPlaced ? (
                            <span className="twin-tilemenu-agent-coords">[{Math.round(ag.currentCol)}, {Math.round(ag.currentRow)}]</span>
                          ) : (
                            <span className="twin-tilemenu-agent-place-tag">+ Place</span>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="twin-tilemenu-empty-state">No agents created in this floor</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* === 4. ACCIONES DE ZONA === */}
        <div className="twin-tilemenu-divider" />

        {roomOnTile ? (
          <div className="twin-tilemenu-room-actions">
            <div className="twin-tilemenu-room-current" style={{ backgroundColor: 'rgba(88, 28, 135, 0.2)', borderColor: 'rgba(168, 85, 247, 0.2)' }}>
              <div className="twin-tilemenu-action-row-left">
                <span>{roomOnTile.icon || '🏢'}</span>
                <span className="twin-tilemenu-room-current-name">{roomOnTile.name}</span>
              </div>
              <span className="twin-tilemenu-room-current-size">{roomOnTile.width}x{roomOnTile.height}m</span>
            </div>

            <button type="button" onClick={() => { if (onSelectRoom) onSelectRoom(roomOnTile.id); onClose(); }} className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--purple twin-tilemenu-rounded">
              <div className="twin-tilemenu-action-row-left">
                <Layers className="twin-icon-sm" />
                <span>Select & Resize Zone</span>
              </div>
              <span className="twin-tilemenu-vertices-tag">📐 Vertices</span>
            </button>

            {onDeleteRoom && (
              <button
                type="button"
                onClick={() => {
                  setConfirmAction({
                    type: 'room',
                    title: 'Delete Zone / Room?',
                    name: roomOnTile.name,
                    icon: roomOnTile.icon || '🏢',
                    description: `Are you sure you want to delete zone "${roomOnTile.name}" (${roomOnTile.width}x${roomOnTile.height}m)?`,
                    confirmText: 'Delete Zone',
                    onConfirm: () => { onDeleteRoom(roomOnTile.id); onClose(); },
                  });
                }}
                className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--rose twin-tilemenu-rounded"
              >
                <Trash2 className="twin-icon-sm" />
                <span>Delete Zone ({roomOnTile.name})</span>
              </button>
            )}
          </div>
        ) : (
          <div ref={roomTriggerRef} className="twin-tilemenu-submenu-wrap" onMouseEnter={() => handleOpenSubmenu('room')} onMouseLeave={handleCloseSubmenuDelayed}>
            <button
              type="button"
              onClick={() => {
                if (onCreateRoom) onCreateRoom(col, row);
                else if (onStartRoomDraft) onStartRoomDraft(col, row);
                onClose();
              }}
              className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--purple twin-tilemenu-rounded"
            >
              <div className="twin-tilemenu-action-row-left">
                <PlusCircle className="twin-icon-sm" />
                <span>Create Zone</span>
              </div>
              <ChevronRight className="twin-icon-xs" />
            </button>

            {activeSubmenu === 'room' && (
              <div
                className={`twin-tilemenu-room-cascade ${submenuStyle.side === 'right' ? 'twin-tilemenu-room-cascade--right' : 'twin-tilemenu-room-cascade--left'} ${submenuStyle.verticalAlign === 'top' ? 'twin-tilemenu-room-cascade--top' : 'twin-tilemenu-room-cascade--bottom'}`}
                onMouseEnter={() => handleOpenSubmenu('room')}
                onMouseLeave={handleCloseSubmenuDelayed}
              >
                <div className="twin-tilemenu-room-cascade-header">
                  <span>Create Zone at [{col}, {row}]</span>
                  <span className="twin-tilemenu-room-cascade-count">{DEFAULT_ROOM_TEMPLATES.length} templates</span>
                </div>

                <div className="twin-tilemenu-room-cascade-list">
                  <button
                    type="button"
                    onClick={() => { if (onCreateRoom) onCreateRoom(col, row); else if (onStartRoomDraft) onStartRoomDraft(col, row); onClose(); }}
                    className="twin-tilemenu-room-template-btn"
                  >
                    <span className="twin-tilemenu-room-template-icon">💻</span>
                    <div className="twin-tilemenu-room-template-text">
                      <div className="twin-tilemenu-action-row-between">
                        <span className="twin-tilemenu-room-template-name">Standard Workspace</span>
                        <span className="twin-tilemenu-room-template-size">4x4m</span>
                      </div>
                      <span className="twin-tilemenu-room-template-desc">Standard office workstations</span>
                    </div>
                  </button>

                  <div className="twin-tilemenu-divider" />
                  <div className="twin-tilemenu-room-cascade-subtitle">Office Templates</div>

                  {DEFAULT_ROOM_TEMPLATES.map((tmpl) => (
                    <button key={tmpl.type} type="button" onClick={() => { if (onCreateRoom) onCreateRoom(col, row, tmpl); onClose(); }} className="twin-tilemenu-room-template-btn twin-tilemenu-room-template-btn--muted">
                      <span className="twin-tilemenu-room-template-icon">{tmpl.icon}</span>
                      <div className="twin-tilemenu-room-template-text">
                        <div className="twin-tilemenu-action-row-between">
                          <span className="twin-tilemenu-room-template-name">{tmpl.name}</span>
                          <span className="twin-tilemenu-room-template-size">{tmpl.defaultWidth}x{tmpl.defaultHeight}m</span>
                        </div>
                        <span className="twin-tilemenu-room-template-desc">{tmpl.description || tmpl.type}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === 5. ACCIONES SOBRE EL ITEM (si existe) === */}
        {itemOnTile && (
          <>
            <button type="button" onClick={() => { if (onSelectItem) onSelectItem(itemOnTile); onClose(); }} className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--amber twin-tilemenu-full-width">
              <Armchair className="twin-icon-sm" />
              <span>Inspect {itemOnTile.label || itemDef?.name || itemOnTile.type}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const name = itemOnTile.label || itemDef?.name || itemOnTile.type;
                setConfirmAction({
                  type: 'furniture',
                  title: 'Delete Resource?',
                  name,
                  icon: itemDef?.icon || '📦',
                  description: `Are you sure you want to delete "${name}" from tile [${col}, ${row}]?`,
                  confirmText: 'Delete Resource',
                  onConfirm: () => { if (onClearTileFurniture) onClearTileFurniture(col, row); else onClearTile(col, row); onClose(); },
                });
              }}
              className="twin-tilemenu-cascade-item twin-tilemenu-cascade-item--rose twin-tilemenu-full-width"
            >
              <Trash2 className="twin-icon-sm" />
              <span>Delete {itemOnTile.label || itemDef?.name || itemOnTile.type}</span>
            </button>
          </>
        )}
      </div>

      {/* === MODAL DE CONFIRMACIÓN (ya migrado en Grupo B) === */}
      {confirmAction && (
        <ConfirmDeleteModal
          isOpen={Boolean(confirmAction)}
          onClose={() => { setConfirmAction(null); onClose(); }}
          onConfirm={() => { if (confirmAction) { confirmAction.onConfirm(); setConfirmAction(null); } }}
          title={confirmAction.title}
          itemName={confirmAction.name}
          itemIcon={confirmAction.icon}
          itemType={confirmAction.type}
          description={confirmAction.description}
          warningText={confirmAction.warningText}
          confirmText={confirmAction.confirmText}
        />
      )}
    </div>
  );
};